import { AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShop } from '../../../shared/store';
import { refundService, type Refund } from '../../../shared/services/refundService';

interface PendingRefundRequest {
  orderId: string;
  reason: string;
  amount: number;
}

const CustomerRefundPage = () => {
  const { t } = useTranslation();
  const { orders } = useShop();
  const [refunds, setRefunds] = useState<Refund[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [listError, setListError] = useState('');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [pendingRefundRequest, setPendingRefundRequest] = useState<PendingRefundRequest | null>(null);

  const resolveOrderTitle = useMemo(
    () =>
      (order: { orderName?: string; items?: Array<{ product?: { nameKey?: string } }> }) => {
        const explicitName = typeof order.orderName === 'string' ? order.orderName.trim() : '';
        if (explicitName) {
          return explicitName;
        }

        const itemNames = (order.items ?? [])
          .map((item) => (typeof item.product?.nameKey === 'string' ? item.product.nameKey.trim() : ''))
          .filter(Boolean)
          .map((nameKey) => t(nameKey));

        if (itemNames.length === 0) {
          return 'Order';
        }

        return itemNames.join(', ');
      },
    [t],
  );

  useEffect(() => {
    loadRefunds();
  }, []);

  const loadRefunds = async () => {
    try {
      setIsLoading(true);
      setListError('');
      const data = await refundService.getMyRefunds();
      setRefunds(data);
    } catch (err) {
      setListError('Failed to load refunds');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestRefund = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrderId || !reason || !amount) {
      setFormError('Please fill all fields');
      return;
    }

    const trimmedReason = reason.trim();
    if (trimmedReason.length < 10) {
      setFormError('Reason must be at least 10 characters');
      return;
    }

    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setFormError('Please enter a valid refund amount');
      return;
    }

    const selectedOrder = orders.find((order) => order.id === selectedOrderId);
    if (selectedOrder && parsedAmount > selectedOrder.total) {
      setFormError('Refund amount cannot exceed order total');
      return;
    }

    setFormError('');
    setFormSuccess('');
    setPendingRefundRequest({
      orderId: selectedOrderId,
      reason: trimmedReason,
      amount: parsedAmount,
    });
    setShowTermsPopup(true);
  };

  const submitPendingRefund = async () => {
    if (!pendingRefundRequest) {
      return;
    }

    try {
      setIsLoading(true);
      setFormError('');
      setFormSuccess('');
      await refundService.requestRefund(
        pendingRefundRequest.orderId,
        pendingRefundRequest.reason,
        pendingRefundRequest.amount,
        true,
      );
      setFormSuccess('Refund request submitted successfully');
      setSelectedOrderId('');
      setReason('');
      setAmount('');
      setPendingRefundRequest(null);
      setShowTermsPopup(false);
      setShowRequestModal(false);
      await loadRefunds();
    } catch (err: unknown) {
      if (err instanceof Error && err.message.trim()) {
        setFormError(err.message);
      } else {
        setFormError('Failed to request refund');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'processed':
        return <CheckCircle className="h-5 w-5 text-blue-600" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-50 border-green-200';
      case 'rejected':
        return 'bg-red-50 border-red-200';
      case 'processed':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-yellow-50 border-yellow-200';
    }
  };

  return (
    <div className="space-y-6">
      {showTermsPopup && pendingRefundRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-orange-700">Refund Terms & Conditions</h3>
            <p className="mt-3 text-sm text-slate-700">
              Refund processing may take between <span className="font-semibold">3 months and 6 months</span>.
              By clicking <span className="font-semibold">Accept Conditions</span>, you confirm you understand and
              accept this timeline.
            </p>
            <p className="mt-3 text-xs text-slate-500">
              Order: {pendingRefundRequest.orderId} | Amount: $ {pendingRefundRequest.amount.toFixed(2)}
            </p>

            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={submitPendingRefund}
                disabled={isLoading}
                className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:opacity-50"
              >
                {isLoading ? 'Submitting...' : 'Accept Conditions'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (isLoading) {
                    return;
                  }
                  setShowTermsPopup(false);
                  setPendingRefundRequest(null);
                }}
                disabled={isLoading}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-orange-100 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">My Refund Requests</h2>
            <p className="mt-2 text-sm text-slate-600">Track the status of your refund requests</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setFormError('');
              setFormSuccess('');
              setShowRequestModal(true);
            }}
            disabled={isLoading}
            className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:opacity-50"
          >
            New Refund
          </button>
        </div>

        {listError && (
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4" />
            {listError}
          </div>
        )}

        {formSuccess && (
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-700">
            <CheckCircle className="h-4 w-4" />
            {formSuccess}
          </div>
        )}

        {isLoading ? (
          <p className="mt-4 text-slate-600">Loading refunds...</p>
        ) : refunds.length === 0 ? (
          <p className="mt-4 text-slate-600">No refund requests yet</p>
        ) : (
          <div className="mt-6 space-y-4">
            {refunds.map((refund, index) => (
              <div key={`${refund.id}-${refund.createdAt}-${index}`} className={`rounded-lg border p-4 ${getStatusColor(refund.status)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {getStatusIcon(refund.status)}
                    <div>
                      <p className="font-semibold text-slate-900">Order {refund.orderId}</p>
                      <p className="text-sm text-slate-600">Amount: $ {refund.amount.toFixed(2)}</p>
                      <p className="text-sm text-slate-600">Status: {refund.status.toUpperCase()}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Requested: {new Date(refund.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
                {refund.adminResponse && (
                  <div className="mt-3 rounded bg-white/50 p-2 text-sm text-slate-700">
                    <p className="font-semibold">Admin Response:</p>
                    <p>{refund.adminResponse}</p>
                  </div>
                )}
                <p className="mt-2 text-xs text-slate-600">Reason: {refund.reason}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {showRequestModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">Request a Refund</h3>
                <p className="mt-1 text-sm text-slate-600">Submit a refund request for your orders</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (isLoading) {
                    return;
                  }
                  setShowRequestModal(false);
                }}
                disabled={isLoading}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
              >
                Close
              </button>
            </div>

            <form onSubmit={handleRequestRefund} className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700">Select Order</label>
                <select
                  value={selectedOrderId}
                  onChange={(e) => setSelectedOrderId(e.target.value)}
                  className="mt-2 w-full rounded-lg border border-orange-200 px-4 py-2 focus:border-orange-400 focus:outline-none"
                >
                  <option value="">Choose an order...</option>
                  {orders.map((order, index) => (
                    <option key={`${order.id}-${order.createdAt}-${index}`} value={order.id}>
                      {resolveOrderTitle(order)} - RWF {order.total.toFixed(2)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700">Refund Amount</label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="mt-2 w-full rounded-lg border border-orange-200 px-4 py-2 focus:border-orange-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700">Reason for Refund</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Explain why you need a refund..."
                  rows={4}
                  className="mt-2 w-full rounded-lg border border-orange-200 px-4 py-2 focus:border-orange-400 focus:outline-none"
                />
              </div>

              {formError && (
                <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4" />
                  {formError}
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="rounded-lg bg-orange-500 px-4 py-2 font-semibold text-white transition hover:bg-orange-600 disabled:opacity-50"
                >
                  {isLoading ? 'Submitting...' : 'Review Terms'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (isLoading) {
                      return;
                    }
                    setShowRequestModal(false);
                  }}
                  disabled={isLoading}
                  className="rounded-lg border border-slate-300 px-4 py-2 font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerRefundPage;
